import type { BadgeVariant } from '@/shared/ui';
import type { AgentTask, AgentFactKind } from '../types';

// Plain-language names for notebook entries. The practice should never see the internal kind strings.
export const FACT_KIND_LABELS: Record<AgentFactKind, string> = {
  fact: 'About your practice',
  preference: 'How you like things',
  correction: 'A correction',
};

export const STATE_LABEL: Record<AgentTask['state'], string> = {
  ready: 'Queued',
  in_progress: 'Working',
  awaiting_approval: 'Needs your approval',
  blocked: 'Blocked',
  needs_review: 'Needs review',
  done: 'Done',
  cancelled: 'Rejected',
  failed: 'Failed',
};

// Semantic color per state, reusing Badge's existing variant palette — no new colors invented.
export const STATE_VARIANT: Record<AgentTask['state'], BadgeVariant> = {
  ready: 'default',
  in_progress: 'info',
  awaiting_approval: 'primary',
  blocked: 'warning',
  needs_review: 'warning',
  done: 'success',
  cancelled: 'error',
  failed: 'error',
};

// Plain-language names for skillKey — the practice should never see the internal key. One label per
// skill (not per finding kind): site_technical_fix, for instance, drafts fixes across several finding
// kinds (title/meta, schema, Core Web Vitals) under one skill, so it gets one shared label.
export const SKILL_LABEL: Record<string, string> = {
  gbp_listing_edit: 'GBP listing',
  gbp_post: 'GBP Post',
  gsc_content_gap: 'Content',
  site_technical_fix: 'Site fix',
  site_page_edit: 'Site fix',
  ai_visibility_fix: 'AI visibility',
  access_request: 'Access request',
  gbp_audit: 'GBP audit',
  seo_visibility_audit: 'SEO audit',
  site_audit: 'Site audit',
  task_generation: 'Task planning',
  practice_ad_strategy: 'Ad strategy',
  ad_access_tracking: 'Ad access',
  ad_campaign_build: 'Campaign setup',
  ad_targeting: 'Targeting',
  ad_conversion_destination: 'Landing page',
  dental_meta_ad_creatives: 'Meta ads',
  google_search_creative: 'Search ads',
  ad_compliance_qa: 'Compliance check',
  ad_launch_gate: 'Go-live',
  ad_strategy_review: 'Ad strategy review',
  ad_reporting: 'Ads report',
  // Reviews (Betty)
  review_program_setup: 'Texting setup',
  review_funnel_audit: 'Funnel check',
  front_desk_routine_coach: 'Front desk routine',
  send_timing_tune: 'Send timing',
  invite_copy_tune: 'Text wording',
  private_feedback_recovery: 'Call back',
  review_funnel_report: 'Reviews report',
};

export function skillLabel(skillKey: string): string {
  return SKILL_LABEL[skillKey] ?? skillKey;
}

// The detail pane's primary tab: a report for a read-only audit run, a draft for actionable work.
export const BUCKET_TAB_LABEL: Record<AgentTask['bucket'], string> = {
  audit: 'Report',
  action: 'Draft',
};

// Inbox ordering: what needs the user first, then work in flight, then anything closed. Within a
// bucket the server's order is preserved (stable sort).
const RANK: Record<AgentTask['state'], number> = {
  awaiting_approval: 0,
  needs_review: 1,
  in_progress: 2,
  ready: 3,
  blocked: 4,
  done: 5,
  cancelled: 6,
  failed: 7,
};

export function sortTasksForInbox(tasks: AgentTask[]): AgentTask[] {
  return [...tasks].sort((a, b) => RANK[a.state] - RANK[b.state]);
}
